import FeatherIcon from 'feather-icons-react';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { VideoProject, videoProjectService } from 'services/videoService';

const VideoProjects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        try {
          const projectsData = await videoProjectService.getAll();

          if (Array.isArray(projectsData)) {
            setProjects(projectsData);
          } else {
            console.warn('API response is not an array');
          }
        } catch (apiError) {
          console.warn('API call failed:', apiError);
        }
      } catch (err) {
        setError('Erro ao carregar projetos. Tente novamente.');
        console.error('Error fetching projects:', err);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  /*const handleDeleteProject = async (projectId: number) => {
    if (window.confirm('Tem certeza que deseja excluir este projeto?')) {
      try {
        await videoProjectService.delete(projectId);
        const updatedProjects = await videoProjectService.getAll();
        setProjects(Array.isArray(updatedProjects) ? updatedProjects : []);
      } catch (err) {
        console.error('Error deleting project:', err);
        setError('Erro ao excluir projeto. Tente novamente.');
      }
    }
  };
*/
  // ✅ Visualizar/Mapeamento
  const handleWatchVideo = (projectId: number) => {
    navigate(`/app/video/watch?projectId=${projectId}`);
  };

  // ✅ ShapeFile
  /*const handleShapeFile = (projectId: number) => {
    console.log('ShapeFile clicked for project:', projectId);
    // TODO: Implementar funcionalidade de shapefile
    // navigate(`/app/video/shapefile?projectId=${projectId}`);
  };*/

  // ✅ Editar (quando não tem imagens)
  const handleEditImages = (projectId: number) => {
    navigate(`/app/video/images/upload?projectId=${projectId}`);
  };

  const handleRefreshProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const projectsData = await videoProjectService.getAll();
      if (Array.isArray(projectsData)) {
        setProjects(projectsData);
      } else {
        console.warn('API response is not an array');
        setProjects([]);
      }
    } catch (err) {
      console.error('Error refreshing projects:', err);
      setError('Erro ao atualizar projetos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
          <p className="mt-2 text-muted">Carregando projetos...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid>
        <Alert variant="danger" className="mt-3">
          <FeatherIcon icon="alert-circle" size={16} className="me-2" />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-2">Capturas</h2>
              <p className="text-muted mb-0">Capturas de vídeo</p>
            </div>
            <Button
              variant="outline-primary"
              onClick={handleRefreshProjects}
              disabled={loading}
              title="Atualizar lista de projetos"
            >
              <FeatherIcon icon="refresh-cw" size={16} className="me-2" />
              {loading ? 'Carregando...' : 'Atualizar'}
            </Button>
          </div>
        </Col>
      </Row>

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-body-tertiary">
              <tr>
                <th className="border-0 ps-3">Captura</th>
                <th className="border-0">Imagens</th>
                <th className="border-0">Status</th>
                <th className="border-0 text-end pe-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {!projects || projects.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    <div className="text-muted">
                      <FeatherIcon icon="video" size={48} className="mb-3" />
                      <p className="mb-0">Nenhum projeto encontrado</p>
                      <small>
                        Crie seu primeiro projeto de análise de vídeo
                      </small>
                    </div>
                  </td>
                </tr>
              ) : (
                projects.map(project => {
                  // ✅ NOVA LÓGICA DOS BOTÕES
                  const hasImages = project.total_images > 0;
                  const isEmpty = project.total_images === 0;

                  return (
                    <tr key={project.id}>
                      <td className="ps-3">
                        <div>
                          <h6 className="mb-1 fw-semibold">{project.name}</h6>
                          {project.description && (
                            <p
                              className="mb-0 text-muted small"
                              style={{ fontSize: '0.875rem' }}
                            >
                              {project.description}
                            </p>
                          )}
                          <small className="text-muted">
                            Criado por {project.user.name}
                          </small>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FeatherIcon
                            icon={hasImages ? 'image' : 'image-off'}
                            size={16}
                            className={`me-2 ${
                              hasImages ? 'text-success' : 'text-muted'
                            }`}
                          />
                          <span
                            className={`fw-semibold ${
                              hasImages ? 'text-success' : 'text-muted'
                            }`}
                          >
                            {project.total_images}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {project.synced ? (
                            <>
                              <FeatherIcon
                                icon="check-circle"
                                size={16}
                                className="me-2 text-success"
                              />
                              <span className="text-success small">
                                Sincronizado
                              </span>
                            </>
                          ) : (
                            <>
                              <FeatherIcon
                                icon="clock"
                                size={16}
                                className="me-2 text-warning"
                              />
                              <span className="text-warning small">
                                Pendente
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="text-end pe-3">
                        <div className="d-flex justify-content-end gap-2">
                          {/* ✅ EDITAR - Apenas quando NÃO tem imagens */}
                          {isEmpty && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditImages(project.id)}
                              title="Editar projeto e adicionar imagens"
                              style={{ width: '110px' }}
                            >
                              <FeatherIcon
                                icon="edit"
                                size={14}
                                className="me-1"
                              />
                              Editar
                            </Button>
                          )}

                          {/* ✅ MAPEAMENTO - Apenas quando TEM imagens */}
                          {hasImages && (
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleWatchVideo(project.id)}
                              title="Visualizar e mapear sinalizações"
                              style={{ width: '130px' }}
                            >
                              <FeatherIcon
                                icon="map-pin"
                                size={14}
                                className="me-1"
                              />
                              Mapeamento
                            </Button>
                          )}

                          {/* ✅ COMPARTILHAR - Apenas quando TEM imagens */}
                          {/*hasImages && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => {
                                const publicUrl = `/public/project-map/${project.id}`;
                                window.open(publicUrl, '_blank');
                              }}
                              title="Compartilhar visualização pública do projeto"
                              style={{ width: '130px' }}
                            >
                              <FeatherIcon
                                icon="share-2"
                                size={14}
                                className="me-1"
                              />
                              Compartilhar
                            </Button>
                          )*/}

                          {/* ✅ SHAPEFILE - Apenas quando TEM imagens */}
                          {/*hasImages && (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleShapeFile(project.id)}
                              title="Gerar arquivo ShapeFile"
                              style={{ width: '110px' }}
                            >
                              <FeatherIcon
                                icon="map"
                                size={14}
                                className="me-1"
                              />
                              ShapeFile
                            </Button>
                          )*/}

                          {/* ✅ DELETAR - Sempre disponível */}
                          {/*<Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteProject(project.id)}
                            title="Excluir projeto"
                          >
                            <FeatherIcon icon="trash-2" size={14} />
                          </Button>*/}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>

        <Card.Footer className="border-top d-flex justify-content-between align-items-center">
          <small className="text-muted">
            Mostrando {projects?.length || 0} de {projects?.length || 0}{' '}
            projetos
          </small>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" disabled>
              <FeatherIcon icon="chevron-left" size={14} className="me-1" />
              Anterior
            </Button>
            <Button variant="outline-secondary" size="sm" disabled>
              Próximo
              <FeatherIcon icon="chevron-right" size={14} className="ms-1" />
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default VideoProjects;
